import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { hooks } from '../hooks';
import { text } from '../text';
import { theme } from '../constants';
import { components } from '../components';
import axiosInstance from '../services/axiosInstance';
import { listCache } from '../utils/listCache';
import { useLanguage } from '../context/LanguageContext';
import {
  MdLocationOn, MdCalendarToday, MdPersonAdd, MdClose, MdDelete,
  MdChevronLeft, MdChevronRight, MdPerson, MdAccessTime
} from 'react-icons/md';

const LocationIcon = MdLocationOn as any;
const CalendarIcon = MdCalendarToday as any;
const PersonAddIcon = MdPersonAdd as any;
const CloseIcon = MdClose as any;
const DeleteIcon = MdDelete as any;
const ChevronLeftIcon = MdChevronLeft as any;
const ChevronRightIcon = MdChevronRight as any;
const PersonIcon = MdPerson as any;
const TimeIcon = MdAccessTime as any;

const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL || '';

export const DetailEventActivity: React.FC = () => {
  const { state } = useLocation();
  const navigate = hooks.useAppNavigate();
  const { t, language } = useLanguage();
  const te = (t as any).eventActivity || {};

  const activityId = state?.activityId;
  const { authSlice } = hooks.useAppSelector((s: any) => s);

  const [activity, setActivity] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [images, setImages] = useState<string[]>([]);
  const [participants, setParticipants] = useState<any[]>([]);
  const [currentImageIdx, setCurrentImageIdx] = useState(0);

  const canDelete = !!activity && (
    String(authSlice.userId) === String(activity.user_id) ||
    authSlice.permissions?.includes('activity:view_all')
  );

  // Participant form
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [participantName, setParticipantName] = useState('');
  const [participantPosition, setParticipantPosition] = useState('');
  const [participantUserId, setParticipantUserId] = useState<number | null>(null);
  const [addingParticipant, setAddingParticipant] = useState(false);

  // User search for participant picker
  const [userSearch, setUserSearch] = useState('');
  const [usersList, setUsersList] = useState<any[]>([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // Lightbox
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(0);

  // Carousel swipe
  const carouselStripRef = useRef<HTMLDivElement>(null);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const isHorizontalSwipeRef = useRef<boolean | null>(null);
  const swipedRef = useRef(false);

  // Delete confirm modal
  const [deleteConfig, setDeleteConfig] = useState<{
    isOpen: boolean; type: 'event' | 'participant' | null; id: number | null;
  }>({ isOpen: false, type: null, id: null });

  useEffect(() => {
    if (!showAddParticipant) {
      setUserSearch('');
      setParticipantName('');
      setParticipantPosition('');
      setParticipantUserId(null);
      setShowUserDropdown(false);
      return;
    }
    if (usersList.length > 0) return;
    setLoadingUsers(true);
    axiosInstance.get('/users?limit=200')
      .then(res => setUsersList(res.data?.data || res.data || []))
      .catch(e => console.error('Failed to load users:', e))
      .finally(() => setLoadingUsers(false));
  }, [showAddParticipant]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!activityId) { navigate(-1); return; }
    const fetch = async () => {
      try {
        const res = await axiosInstance.get(`/event-activities/${activityId}`);
        const data = res.data;
        setActivity(data);
        setImages((data.images || []).map((img: any) => img.file_path || img));
        setParticipants(data.participants || []);
      } catch (err) {
        console.error('Failed to fetch event activity:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [activityId]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr.slice(0, 10) + 'T00:00:00');
    return d.toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatTime = (dateStr: string) => {
    if (!dateStr || dateStr.length < 16) return '';
    const d = new Date(dateStr.slice(0, 16));
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const prevImage = () => setCurrentImageIdx(prev => (prev - 1 + images.length) % images.length);
  const nextImage = () => setCurrentImageIdx(prev => (prev + 1) % images.length);
  const openLightbox = (idx: number) => { setLightboxIdx(idx); setLightboxOpen(true); };
  const prevLightbox = () => setLightboxIdx(prev => (prev - 1 + images.length) % images.length);
  const nextLightbox = () => setLightboxIdx(prev => (prev + 1) % images.length);

  const handleCarouselTouchStart = (e: React.TouchEvent) => {
    if (images.length <= 1) return;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    swipedRef.current = false;
    isHorizontalSwipeRef.current = null;
    if (carouselStripRef.current) carouselStripRef.current.style.transition = 'none';
  };

  const handleCarouselTouchMove = (e: React.TouchEvent) => {
    if (images.length <= 1) return;
    const deltaX = e.touches[0].clientX - touchStartXRef.current;
    const deltaY = e.touches[0].clientY - touchStartYRef.current;
    if (isHorizontalSwipeRef.current === null) {
      isHorizontalSwipeRef.current = Math.abs(deltaX) > Math.abs(deltaY);
    }
    if (!isHorizontalSwipeRef.current) return;
    if (carouselStripRef.current) {
      carouselStripRef.current.style.transform = `translateX(calc(-33.333% + ${deltaX}px))`;
    }
  };

  const handleCarouselTouchEnd = (e: React.TouchEvent) => {
    if (images.length <= 1 || !isHorizontalSwipeRef.current) return;
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    if (Math.abs(deltaX) > 60) {
      swipedRef.current = true;
      const goNext = deltaX < 0;
      if (carouselStripRef.current) {
        carouselStripRef.current.style.transition = 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        carouselStripRef.current.style.transform = goNext ? 'translateX(-66.666%)' : 'translateX(0%)';
      }
      setTimeout(() => {
        if (goNext) nextImage(); else prevImage();
        requestAnimationFrame(() => {
          if (carouselStripRef.current) {
            carouselStripRef.current.style.transition = 'none';
            carouselStripRef.current.style.transform = 'translateX(-33.333%)';
          }
        });
      }, 280);
    } else {
      if (carouselStripRef.current) {
        carouselStripRef.current.style.transition = 'transform 0.28s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
        carouselStripRef.current.style.transform = 'translateX(-33.333%)';
      }
    }
  };

  const selectUser = (user: any) => {
    setParticipantUserId(user.id);
    setParticipantName(user.name || '');
    setUserSearch(user.name || '');
    setShowUserDropdown(false);
  };

  const clearSelectedUser = () => {
    setParticipantUserId(null);
    setParticipantName('');
    setUserSearch('');
    setShowUserDropdown(false);
  };

  const filteredUsers = usersList.filter(u => {
    const q = userSearch.toLowerCase();
    return !q || (u.name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  }).slice(0, 8);

  const handleAddParticipant = async () => {
    if (!participantName.trim()) return;
    setAddingParticipant(true);
    try {
      const res = await axiosInstance.post(`/event-activities/${activityId}/participants`, {
        name: participantName.trim(),
        position: participantPosition.trim() || undefined,
        ...(participantUserId ? { user_id: participantUserId } : {}),
      });
      setParticipants(prev => [...prev, res.data]);
      setShowAddParticipant(false);
    } catch (err) {
      console.error('Failed to add participant:', err);
    } finally {
      setAddingParticipant(false);
    }
  };

  const executeDelete = async () => {
    const { type, id } = deleteConfig;
    if (!id) return;
    try {
      if (type === 'participant') {
        await axiosInstance.delete(`/event-activities/${activityId}/participants/${id}`);
        setParticipants(prev => prev.filter(p => p.id !== id));
      } else if (type === 'event') {
        await axiosInstance.delete(`/event-activities/${id}`);
        const cache = listCache.restore('event-activities');
        if (cache) {
          listCache.save('event-activities', {
            ...cache,
            items: cache.items.filter((item: any) => item.id !== id),
          });
        }
        navigate('/EventActivity', { replace: true });
      }
    } catch (err) {
      console.error(`Failed to delete ${type}:`, err);
    } finally {
      setDeleteConfig({ isOpen: false, type: null, id: null });
    }
  };

  const renderLightbox = () => {
    if (!lightboxOpen || images.length === 0) return null;
    const url = `${IMAGE_BASE_URL}${images[lightboxIdx]}`;
    return (
      <div
        onClick={() => setLightboxOpen(false)}
        style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        {/* Close */}
        <div onClick={() => setLightboxOpen(false)} style={{ position: 'absolute', top: 20, right: 20, cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 8, display: 'flex' }}>
          <CloseIcon size={22} color="white" />
        </div>

        {/* Counter */}
        <div style={{ position: 'absolute', top: 24, left: '50%', transform: 'translateX(-50%)', backgroundColor: 'rgba(0,0,0,0.5)', color: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: '600' }}>
          {lightboxIdx + 1} / {images.length}
        </div>

        {/* Prev */}
        {images.length > 1 && (
          <div onClick={e => { e.stopPropagation(); prevLightbox(); }} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 10, cursor: 'pointer', display: 'flex', zIndex: 1 }}>
            <ChevronLeftIcon size={28} color="white" />
          </div>
        )}

        {/* Image */}
        <img
          src={url}
          alt=""
          onClick={e => e.stopPropagation()}
          style={{ maxWidth: '90vw', maxHeight: '85vh', objectFit: 'contain', borderRadius: 8 }}
        />

        {/* Next */}
        {images.length > 1 && (
          <div onClick={e => { e.stopPropagation(); nextLightbox(); }} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '50%', padding: 10, cursor: 'pointer', display: 'flex', zIndex: 1 }}>
            <ChevronRightIcon size={28} color="white" />
          </div>
        )}
      </div>
    );
  };

  const renderDeleteModal = () => {
    if (!deleteConfig.isOpen) return null;
    const title = deleteConfig.type === 'event' ? te.deleteTitle : te.deleteParticipantTitle;
    const message = deleteConfig.type === 'event' ? te.deleteConfirm : te.deleteParticipantConfirm;
    return (
      <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 20 }}>
        <div style={{ backgroundColor: 'white', borderRadius: 20, padding: 24, width: '100%', maxWidth: 320, textAlign: 'center', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
          <div style={{ width: 52, height: 52, borderRadius: '50%', backgroundColor: '#FEE2E2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <DeleteIcon size={24} color="#EF4444" />
          </div>
          <text.H4 style={{ marginBottom: 10 }}>{title || 'Delete'}</text.H4>
          <text.T16 style={{ color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>{message}</text.T16>
          <div style={{ display: 'flex', gap: 12 }}>
            <components.Button title={t.common?.cancel || 'Cancel'} style={{ backgroundColor: '#F3F4F6', color: '#4B5563', padding: 10 }} onClick={() => setDeleteConfig({ isOpen: false, type: null, id: null })} />
            <components.Button title={t.common?.delete || 'Delete'} style={{ backgroundColor: '#EF4444', padding: 10 }} onClick={executeDelete} />
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (loading) {
      return (
        <main className="container" style={{ marginTop: 60, height: '100vh', overflowY: 'auto', paddingBottom: 40 }}>
          <text.T16 style={{ textAlign: 'center', marginTop: 60, color: '#9CA3AF' }}>{t.common?.loading || 'Loading...'}</text.T16>
        </main>
      );
    }

    if (!activity) {
      return (
        <main className="container" style={{ marginTop: 60, height: '100vh', overflowY: 'auto', paddingBottom: 40 }}>
          <text.T16 style={{ textAlign: 'center', marginTop: 60, color: '#9CA3AF' }}>Event not found.</text.T16>
        </main>
      );
    }

    return (
      <main className="container" style={{ marginTop: 60, height: '100vh', overflowY: 'auto' }}>

        {/* Image Carousel */}
        {images.length > 0 && (
          <div style={{ position: 'relative', marginBottom: 20, borderRadius: 16, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', flexShrink: 0 }}>

            {/* 3-slot swipeable strip: [prev][current][next] */}
            <div
              ref={carouselStripRef}
              style={{
                display: 'flex',
                width: '300%',
                height: 320,
                transform: 'translateX(-33.333%)',
                willChange: 'transform',
                userSelect: 'none',
                WebkitUserSelect: 'none',
                cursor: 'pointer',
              } as React.CSSProperties}
              onTouchStart={handleCarouselTouchStart}
              onTouchMove={handleCarouselTouchMove}
              onTouchEnd={handleCarouselTouchEnd}
              onClick={() => { if (!swipedRef.current) openLightbox(currentImageIdx); }}
            >
              {/* Prev slot */}
              <div style={{ width: '33.333%', height: '100%', backgroundColor: '#000', flexShrink: 0 }}>
                <img
                  src={`${IMAGE_BASE_URL}${images[(currentImageIdx - 1 + images.length) % images.length]}`}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  draggable={false}
                />
              </div>
              {/* Current slot */}
              <div style={{ width: '33.333%', height: '100%', backgroundColor: '#000', flexShrink: 0, position: 'relative' }}>
                <img
                  src={`${IMAGE_BASE_URL}${images[currentImageIdx]}`}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  draggable={false}
                />
                {images.length > 1 && (
                  <div style={{ position: 'absolute', top: 12, right: 12, backgroundColor: 'rgba(0,0,0,0.55)', color: 'white', padding: '4px 10px', borderRadius: 16, fontSize: 12, fontWeight: '700', pointerEvents: 'none' }}>
                    {currentImageIdx + 1} / {images.length}
                  </div>
                )}
              </div>
              {/* Next slot */}
              <div style={{ width: '33.333%', height: '100%', backgroundColor: '#000', flexShrink: 0 }}>
                <img
                  src={`${IMAGE_BASE_URL}${images[(currentImageIdx + 1) % images.length]}`}
                  alt=""
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  draggable={false}
                />
              </div>
            </div>

            {/* Nav buttons */}
            {images.length > 1 && (
              <>
                <div onClick={e => { e.stopPropagation(); prevImage(); }} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: '50%', padding: 8, cursor: 'pointer', display: 'flex', zIndex: 2 }}>
                  <ChevronLeftIcon size={22} color="white" />
                </div>
                <div onClick={e => { e.stopPropagation(); nextImage(); }} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', backgroundColor: 'rgba(0,0,0,0.45)', borderRadius: '50%', padding: 8, cursor: 'pointer', display: 'flex', zIndex: 2 }}>
                  <ChevronRightIcon size={22} color="white" />
                </div>
              </>
            )}

            {/* Thumbnail strip */}
            {images.length > 1 && (
              <div style={{ display: 'flex', gap: 6, padding: '8px 12px', backgroundColor: 'rgba(0,0,0,0.75)', overflowX: 'auto' }}>
                {images.map((img, idx) => (
                  <div key={idx} onClick={e => { e.stopPropagation(); setCurrentImageIdx(idx); }} style={{ width: 44, height: 44, flexShrink: 0, borderRadius: 6, overflow: 'hidden', border: `2px solid ${idx === currentImageIdx ? '#6366F1' : 'transparent'}`, cursor: 'pointer', opacity: idx === currentImageIdx ? 1 : 0.6, transition: 'all 0.2s' }}>
                    <img src={`${IMAGE_BASE_URL}${img}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Event info card */}
        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 20, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          {/* Header row */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <div style={{ padding: '3px 10px', borderRadius: 20, background: 'linear-gradient(135deg, #6366F1, #4F46E5)', display: 'inline-block' }}>
                <text.T14 style={{ color: 'white', fontSize: 11, fontWeight: '700', letterSpacing: 0.5 }}>
                  EVENT
                </text.T14>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <CalendarIcon size={16} color="#6366F1" />
              <text.T14 style={{ color: '#6366F1', fontWeight: '600', fontSize: 14 }}>
                {formatDate(activity.date)}
              </text.T14>
              {formatTime(activity.date) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, backgroundColor: '#EEF2FF', borderRadius: 8, padding: '2px 8px' }}>
                  <TimeIcon size={13} color="#6366F1" />
                  <text.T14 style={{ color: '#6366F1', fontWeight: '700', fontSize: 13 }}>
                    {formatTime(activity.date)}
                  </text.T14>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {activity.description && (
            <div style={{ marginBottom: 16, padding: '12px 14px', backgroundColor: '#F9FAFB', borderRadius: 10, borderLeft: '3px solid #6366F1' }}>
              <text.T14 style={{ color: theme.colors.mainDark, lineHeight: 1.6, whiteSpace: 'pre-wrap', textTransform: 'capitalize' }}>
                {activity.description}
              </text.T14>
            </div>
          )}

          {/* Location */}
          {activity.address && (
            <div
              onClick={() => { if (activity.location) window.open(`https://www.google.com/maps/search/?api=1&query=${activity.location}`, '_blank'); }}
              style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 12px', backgroundColor: '#F0FDF4', borderRadius: 10, border: '1px solid #A7F3D0', cursor: activity.location ? 'pointer' : 'default' }}
            >
              <LocationIcon size={18} color="#10B981" style={{ marginTop: 2, flexShrink: 0 }} />
              <div>
                <text.T14 style={{ color: '#065F46', fontWeight: '500', display: 'block', lineHeight: 1.5 }}>{activity.address}</text.T14>
                {activity.location && <text.T14 style={{ color: '#6B7280', fontSize: 11, marginTop: 2, display: 'block' }}>{activity.location}</text.T14>}
              </div>
            </div>
          )}

          {/* Creator */}
          {activity.user_name && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 14, paddingTop: 14, borderTop: '1px solid #F3F4F6' }}>
              <div style={{ width: 30, height: 30, borderRadius: '50%', backgroundColor: '#E0E7FF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <PersonIcon size={16} color="#6366F1" />
              </div>
              <div>
                <text.T14 style={{ fontSize: 11, color: '#9CA3AF' }}>{te.creator || 'Created by'}</text.T14>
                <text.T14 style={{ fontWeight: '600', color: theme.colors.mainDark, textTransform: 'capitalize', display: 'block', marginTop: 1 }}>{activity.user_name}</text.T14>
              </div>
            </div>
          )}
        </div>

        {/* Participants */}
        <div style={{ backgroundColor: 'white', borderRadius: 16, border: '1px solid #F3F4F6', padding: 20, marginBottom: 60, boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <text.H5 style={{ color: theme.colors.mainDark }}>{te.participants || 'Event Participants'}</text.H5>
              {participants.length > 0 && (
                <div style={{ backgroundColor: '#E0E7FF', borderRadius: 20, padding: '2px 8px' }}>
                  <text.T14 style={{ fontSize: 12, color: '#6366F1', fontWeight: '700' }}>{participants.length}</text.T14>
                </div>
              )}
            </div>
            <div
              onClick={() => setShowAddParticipant(!showAddParticipant)}
              style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer', backgroundColor: showAddParticipant ? '#EEF2FF' : 'transparent', padding: '4px 10px', borderRadius: 8, border: `1px solid ${showAddParticipant ? '#C7D2FE' : 'transparent'}` }}
            >
              <PersonAddIcon size={18} color="#6366F1" />
              <text.T14 style={{ fontWeight: '600', color: '#6366F1' }}>{te.add || 'Add'}</text.T14>
            </div>
          </div>

          {/* Add participant form */}
          {showAddParticipant && (
            <div style={{ padding: '16px 0', borderTop: '1px dashed #E5E7EB', marginBottom: 16 }}>
              {/* User search / picker */}
              <div style={{ position: 'relative', marginBottom: 10 }}>
                {participantUserId ? (
                  // Selected user chip
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 10, border: '1px solid #C7D2FE', backgroundColor: '#EEF2FF' }}>
                    <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'linear-gradient(135deg, #6366F1, #4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <text.T14 style={{ color: 'white', fontSize: 12, fontWeight: '700', textTransform: 'capitalize' }}>{participantName.charAt(0).toUpperCase()}</text.T14>
                    </div>
                    <text.T14 style={{ flex: 1, color: '#4338CA', fontWeight: '600', textTransform: 'capitalize', fontSize: 14 }}>{participantName}</text.T14>
                    <div onClick={clearSelectedUser} style={{ cursor: 'pointer', padding: 2, display: 'flex' }}>
                      <CloseIcon size={16} color="#6366F1" />
                    </div>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder={te.searchUser || 'Search app user...'}
                      value={userSearch}
                      onChange={e => { setUserSearch(e.target.value); setShowUserDropdown(true); }}
                      onFocus={() => setShowUserDropdown(true)}
                      onBlur={() => setTimeout(() => setShowUserDropdown(false), 150)}
                      style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB', outline: 'none', boxSizing: 'border-box', fontSize: 14 }}
                    />
                    {showUserDropdown && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.1)', marginTop: 4, maxHeight: 220, overflowY: 'auto' }}>
                        {loadingUsers ? (
                          <div style={{ padding: '12px 14px' }}>
                            <text.T14 style={{ color: '#9CA3AF', fontSize: 13 }}>{t.common?.loading || 'Loading...'}</text.T14>
                          </div>
                        ) : filteredUsers.length === 0 ? (
                          <div style={{ padding: '12px 14px' }}>
                            <text.T14 style={{ color: '#9CA3AF', fontSize: 13 }}>{te.noUsersFound || 'No users found'}</text.T14>
                          </div>
                        ) : filteredUsers.map(u => (
                          <div
                            key={u.id}
                            onMouseDown={() => selectUser(u)}
                            style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F9FAFB' }}
                          >
                            <div style={{ width: 30, height: 30, borderRadius: '50%', background: 'linear-gradient(135deg, #6366F1, #4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <text.T14 style={{ color: 'white', fontSize: 12, fontWeight: '700', textTransform: 'capitalize' }}>{(u.name || '?').charAt(0).toUpperCase()}</text.T14>
                            </div>
                            <div>
                              <text.T14 style={{ fontWeight: '600', color: '#1F2937', textTransform: 'capitalize', fontSize: 13 }}>{u.name}</text.T14>
                              {u.email && <text.T14 style={{ color: '#9CA3AF', fontSize: 11, display: 'block' }}>{u.email}</text.T14>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <input
                type="text"
                placeholder={te.position || 'Position / Role'}
                value={participantPosition}
                onChange={e => setParticipantPosition(e.target.value)}
                style={{ width: '100%', padding: '10px 14px', borderRadius: 10, border: '1px solid #E5E7EB', marginBottom: 12, outline: 'none', boxSizing: 'border-box', fontSize: 14 }}
              />
              <div style={{ display: 'flex', gap: 10 }}>
                <components.Button title={t.common?.cancel || 'Cancel'} style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }} containerStyle={{ flex: 1 }} onClick={() => setShowAddParticipant(false)} />
                <components.Button title={addingParticipant ? (t.common?.saving || 'Saving...') : (t.common?.save || 'Save')} containerStyle={{ flex: 1 }} onClick={handleAddParticipant} />
              </div>
            </div>
          )}

          {/* Participant list */}
          {participants.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', opacity: 0.6 }}>
              <text.T14 style={{ color: '#9CA3AF' }}>{te.noParticipants || 'No participants added yet.'}</text.T14>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {participants.map((p, idx) => (
                <div key={p.id ?? idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', backgroundColor: '#F9FAFB', borderRadius: 10, border: '1px solid #F3F4F6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #6366F1, #4F46E5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <text.T14 style={{ fontWeight: '800', color: 'white', fontSize: 14, textTransform: 'capitalize' }}>{p.name?.charAt(0)?.toUpperCase()}</text.T14>
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <text.T14 style={{ fontWeight: '600', color: theme.colors.mainDark, textTransform: 'capitalize' }}>{p.name}</text.T14>
                        {p.user_id && (
                          <div style={{ backgroundColor: '#EEF2FF', borderRadius: 6, padding: '1px 6px' }}>
                            <text.T14 style={{ fontSize: 10, color: '#6366F1', fontWeight: '700' }}>APP</text.T14>
                          </div>
                        )}
                      </div>
                      {p.position && <text.T14 style={{ color: '#6B7280', fontSize: 12, display: 'block', marginTop: 1, textTransform: 'capitalize' }}>{p.position}</text.T14>}
                    </div>
                  </div>
                  <div onClick={() => setDeleteConfig({ isOpen: true, type: 'participant', id: p.id })} style={{ padding: 6, cursor: 'pointer' }}>
                    <DeleteIcon size={18} color="#EF4444" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    );
  };

  return (
    <div id="screen" style={{ backgroundColor: '#F9FAFB', minHeight: '100vh' }}>
      <style>{`::-webkit-scrollbar { display: none; }`}</style>
      <components.Header
        title={te.detailTitle || 'Event Detail'}
        goBack={true}
        trash={canDelete}
        onTrash={() => setDeleteConfig({ isOpen: true, type: 'event', id: activity?.id })}
      />
      {renderContent()}
      {renderLightbox()}
      {renderDeleteModal()}
    </div>
  );
};
